<template>
  <div v-if="painelInvalido" class="painel-standalone-state" role="alert">
    <h1>Codigo do painel ausente</h1>
    <p>Abra o endereco com o codigo, por exemplo: #/painel/7.</p>
  </div>

  <main v-else class="painel" :style="painelViewportStyle">
    <section
      ref="soundActivation"
      v-if="soundActivationRequired"
      class="sound-activation"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sound-title"
      tabindex="-1"
      @keydown.tab.prevent="focusSoundActivation"
    >
      <div class="sound-activation-card">
        <h1 id="sound-title">Ativar painel com som</h1>
        <p>Toque no botao enquanto ainda estiver em frente a TV.</p>
        <button
          ref="soundActivationButton"
          class="sound-activation-button"
          type="button"
          :disabled="soundActivating"
          @click="activateSound"
        >
          {{ soundActivating ? 'ATIVANDO...' : 'ATIVAR SOM' }}
        </button>
        <button
          ref="soundSilentButton"
          class="sound-silent-button"
          type="button"
          :disabled="soundActivating"
          @click="startWithoutSound"
        >
          INICIAR SEM SOM · {{ soundCountdownLabel }}
        </button>
        <p class="sound-countdown-help">
          O painel iniciara sem som automaticamente.
        </p>
        <p v-if="soundError" class="sound-error" role="alert">
          {{ soundError }}
        </p>
      </div>
    </section>

    <header class="painel-header">
      <h1>CHAMADAS</h1>
    </header>

    <section class="painel-content" aria-live="polite">
      <div v-if="loading && !pacientes.length" class="painel-state">
        Carregando chamadas...
      </div>

      <template v-else>
        <div v-if="paciente.paciente" class="chamada-atual">
          <p
            class="chamada-paciente"
            :style="{ backgroundColor: paciente.classificacao_risco }"
          >
            {{ paciente.paciente }}
          </p>
          <p v-if="paciente.sala" class="chamada-detalhe">
            SALA: {{ paciente.sala }}
          </p>
        </div>

        <v-data-table
          :headers="headers"
          :items="pacientes"
          class="painel-table elevation-1"
          item-key="_key"
          :hide-actions="true"
        >
          <template slot="no-data">
            <div class="painel-state text-xs-center text-uppercase">
              Aguardando chamada para atendimento.
            </div>
          </template>
          <template v-slot:items="props">
            <tr
              :class="{ 'current-call-row': isCurrentCall(props.item) }"
              :style="{ backgroundColor: props.item.classificacao_risco }"
            >
              <td class="text-uppercase">
                <b>{{ props.item.paciente }}</b>
              </td>
              <td class="text-uppercase"><b>{{ props.item.sala }}</b></td>
              <td class="text-uppercase"><b>{{ props.item.data_hora }}</b></td>
            </tr>
          </template>
        </v-data-table>
      </template>
    </section>

    <footer class="painel-footer">
      <p>{{ data_hora }}</p>
    </footer>

    <div v-if="syncError || !isConnected" class="connection-status" role="status">
      {{ syncError ? 'Sincronizacao indisponivel' : 'Reconectando...' }}
    </div>
  </main>
</template>

<script>
import moment from 'moment'

import { getPainelEstado } from '../services'
import { PainelAudio } from '../utils/painel-audio.mjs'
import { millisecondsUntilNextMinute } from '../utils/painel-clock.mjs'
import { PainelCountdown } from '../utils/painel-countdown.mjs'
import { PainelVoice } from '../utils/painel-voice.mjs'
import {
  PainelAudioRuntime,
  WakeLockController
} from '../utils/painel-runtime.mjs'
import {
  canApplySync,
  chamadaSignature,
  isPainelCodigoValido,
  normalizeSnapshot,
  sameChamadaKey,
  samePainel
} from '../utils/painel-state.mjs'

moment.locale('pt-br')

const emptyPaciente = () => ({
  paciente: '',
  departamento: '',
  sala: '',
  data_hora: '',
  classificacao_risco: '#FFFFFF',
  _key: 'empty'
})

export default {
  name: 'Main',
  data() {
    return {
      data_hora: '',
      painelViewportHeight: null,
      paciente: emptyPaciente(),
      pacientes: [],
      headers: [
        { text: 'PACIENTE', align: 'left', value: 'paciente', sortable: false },
        { text: 'SALA', align: 'left', value: 'sala', sortable: false },
        { text: 'DATA/HORA', align: 'left', value: 'data_hora', sortable: false }
      ],
      loading: true,
      syncError: false,
      isConnected: false,
      soundActivationRequired: true,
      soundActivating: false,
      soundCountdown: 60,
      soundError: '',
      soundMuted: false,
      soundReady: false,
      wakeLockActive: false,
      lastChamadaSignature: ''
    }
  },
  computed: {
    painelViewportStyle() {
      const height = this.painelViewportHeight
      if (!height) return null
      return {
        height: `${height}px`,
        minHeight: `${height}px`
      }
    },
    painelCodigo() {
      return this.$route.params.painel
    },
    painelInvalido() {
      return !isPainelCodigoValido(this.painelCodigo)
    },
    soundCountdownLabel() {
      const minutes = Math.floor(this.soundCountdown / 60)
      const seconds = this.soundCountdown % 60
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }
  },
  watch: {
    soundActivationRequired(required) {
      if (required) this.$nextTick(this.focusSoundActivation)
    },
    soundActivating() {
      this.$nextTick(this.focusSoundActivation)
    }
  },
  created() {
    this._eventGeneration = 0
    this._syncPromise = null
    this._clockTimer = null
    this._syncTimer = null
    this._destroyed = false
    this._audioRuntime = null
    this._soundCountdown = null
    this._wakeLockController = null
    this.$socket.open()
  },
  mounted() {
    this.updateViewportHeight()
    window.addEventListener('resize', this.updateViewportHeight)
    this.getDateHour()
    this.focusSoundActivation()
    this.scheduleClockUpdate()

    if (this.painelInvalido) {
      this.loading = false
      return
    }

    const voice = new PainelVoice({
      speechSynthesis: window.speechSynthesis,
      SpeechSynthesisUtterance: window.SpeechSynthesisUtterance,
      fallback: window.responsiveVoice,
      timerHost: window
    })
    const audioManager = new PainelAudio({
      createAudio: (source) => {
        const audio = new Audio(source)
        audio.preload = 'auto'
        audio.load()
        return audio
      },
      voice,
      storage: window.localStorage,
      alertSource: require('../assets/SD_ALERT_43.mp3'),
      timerHost: window
    })
    this._wakeLockController = new WakeLockController({
      request: 'wakeLock' in navigator
        ? () => navigator.wakeLock.request('screen')
        : null,
      isVisible: () => document.visibilityState === 'visible',
      onActiveChange: (active) => {
        if (!this._destroyed) this.wakeLockActive = active
      },
      onError: (error) => {
        if (process.env.NODE_ENV !== 'production') console.warn(error)
      },
      setTimer: window.setTimeout.bind(window),
      clearTimer: window.clearTimeout.bind(window)
    })
    this._audioRuntime = new PainelAudioRuntime({
      audioManager,
      requestWakeLock: () => {
        this._wakeLockController.resetRetry()
        return this.requestWakeLock()
      },
      onStateChange: ({ ready, activationRequired, muted, error }) => {
        if (this._destroyed) return
        this.soundReady = ready
        this.soundActivationRequired = activationRequired
        this.soundMuted = muted
        if (ready) this._soundCountdown?.stop()
        if (error === 'announcement-failed') {
          this.soundError = 'Falha ao reproduzir a chamada. Toque em ATIVAR SOM para tentar novamente.'
          this._soundCountdown?.resume()
        }
      }
    })
    this._audioRuntime.lastSignature = this.lastChamadaSignature
    this._soundCountdown = new PainelCountdown({
      seconds: 60,
      onTick: (remaining) => { this.soundCountdown = remaining },
      onExpire: this.startWithoutSound,
      setInterval: window.setInterval.bind(window),
      clearInterval: window.clearInterval.bind(window)
    })
    this._soundCountdown.start()

    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    window.addEventListener('online', this.handleOnline)

    this.restoreSound()
    this.requestWakeLock()
    this.syncPacientes()
    this._syncTimer = window.setInterval(this.syncPacientes, 60000)
  },
  beforeDestroy() {
    this._destroyed = true
    window.removeEventListener('resize', this.updateViewportHeight)
    window.clearTimeout(this._clockTimer)
    window.clearInterval(this._syncTimer)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    window.removeEventListener('online', this.handleOnline)
    this._soundCountdown?.destroy()
    this._audioRuntime?.destroy()
    this.releaseWakeLock()
  },
  methods: {
    updateViewportHeight() {
      const height = Number(window.innerHeight)
      if (height > 0) this.painelViewportHeight = height
    },
    getDateHour() {
      this.data_hora = moment().format('LLLL')
    },
    isCurrentCall(item) {
      return sameChamadaKey(item, this.paciente)
    },
    scheduleClockUpdate() {
      window.clearTimeout(this._clockTimer)
      this._clockTimer = window.setTimeout(() => {
        if (this._destroyed) return
        this.getDateHour()
        this.scheduleClockUpdate()
      }, millisecondsUntilNextMinute())
    },
    focusSoundActivation(event) {
      const button = this.$refs.soundActivationButton
      const silentButton = this.$refs.soundSilentButton
      const dialog = this.$refs.soundActivation
      if (event && !this.soundActivating && button && silentButton) {
        const controls = [button, silentButton]
        const current = controls.indexOf(document.activeElement)
        const direction = event.shiftKey ? -1 : 1
        const next = (current + direction + controls.length) % controls.length
        controls[next].focus()
        return
      }
      const target = this.soundActivating ? dialog : button
      if (target) target.focus()
    },
    async restoreSound() {
      const runtime = this._audioRuntime
      const restored = await runtime.restore()
      if (this._destroyed || runtime !== this._audioRuntime) return
      this.soundReady = restored
      this.soundActivationRequired = runtime.activationRequired
    },
    async activateSound() {
      if (this._destroyed || !this._audioRuntime) return
      const runtime = this._audioRuntime
      this.soundActivating = true
      this.soundError = ''
      this._soundCountdown?.pause()
      try {
        const activated = await runtime.activate()
        if (this._destroyed || runtime !== this._audioRuntime) return
        if (!activated) {
          this.soundReady = false
          this.soundActivationRequired = true
          this.soundError = 'Nao foi possivel ativar o som. Verifique o volume e tente novamente.'
          this._soundCountdown?.resume()
          return
        }
        this.soundReady = true
        this.soundActivationRequired = false
        this._soundCountdown?.stop()
      } catch (error) {
        if (this._destroyed || runtime !== this._audioRuntime) return
        console.error('[Painel] Falha ao ativar som', error, error?.cause)
        this.soundReady = false
        this.soundActivationRequired = true
        this.soundError = error?.message || 'Nao foi possivel ativar o som. Verifique o volume e tente novamente.'
        this._soundCountdown?.resume()
      } finally {
        if (!this._destroyed && runtime === this._audioRuntime) {
          this.soundActivating = false
        }
      }
    },
    startWithoutSound() {
      this._soundCountdown?.stop()
      this._audioRuntime?.mute()
      this.soundMuted = true
      this.soundActivationRequired = false
      this.soundError = ''
    },
    async syncPacientes() {
      if (this.painelInvalido) return
      if (this._syncPromise) return this._syncPromise

      const generation = this._eventGeneration
      const request = getPainelEstado(this.$axios, this.painelCodigo)
      this._syncPromise = request

      try {
        const response = await request
        const pacientes = normalizeSnapshot(response.data)
        if (!pacientes) throw new Error('Invalid panel state payload')
        if (canApplySync(generation, this._eventGeneration)) {
          this.applyPacientes(pacientes, false)
        }
        this.syncError = false
      } catch (error) {
        this.syncError = true
        if (process.env.NODE_ENV !== 'production') console.warn(error)
      } finally {
        this.loading = false
        if (this._syncPromise === request) this._syncPromise = null
      }
    },
    applyPacientes(pacientes, announce) {
      const first = pacientes[0] || emptyPaciente()
      const signature = chamadaSignature(first)

      this.pacientes = pacientes
      this.paciente = first

      if (!this._audioRuntime) {
        if (announce) this.lastChamadaSignature = signature
        if (announce) this.soundActivationRequired = true
        return
      }

      this._audioRuntime.applyPaciente(first, signature, announce)
      this.lastChamadaSignature = this._audioRuntime.lastSignature
      if (announce && this._audioRuntime.activationRequired) {
        this.soundActivationRequired = true
      }
    },
    ensureSocket() {
      if (!this.$socket.connected) this.$socket.open()
    },
    handleVisibilityChange() {
      if (document.visibilityState !== 'visible') return
      this._wakeLockController?.resetRetry()
      this.ensureSocket()
      this.requestWakeLock()
      this.syncPacientes()
    },
    handleOnline() {
      this.ensureSocket()
      this.syncPacientes()
    },
    async requestWakeLock() {
      if (this._destroyed) return null
      return this._wakeLockController?.request() ?? null
    },
    async releaseWakeLock() {
      await this._wakeLockController?.destroy()
    }
  },
  sockets: {
    connect() {
      this.isConnected = true
      this.syncPacientes()
    },
    disconnect() {
      this.isConnected = false
    },
    chamada(data) {
      if (!data || !samePainel(data.painel, this.painelCodigo)) return
      const pacientes = normalizeSnapshot(data.pacientes)
      if (!pacientes) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(new Error('Invalid panel socket payload'))
        }
        return
      }

      this._eventGeneration += 1
      this.applyPacientes(pacientes, true)
    }
  }
}
</script>

<style scoped>
.painel {
  width: 100%;
  min-width: 0;
  height: 100vh;
  min-height: 100vh;
  display: grid;
  grid-template-rows: clamp(56px, 9vh, 104px) minmax(0, 1fr) clamp(60px, 11vh, 120px);
  background: #ffffff;
  color: #17212b;
}

@supports (height: 100dvh) {
  .painel {
    height: 100dvh;
    min-height: 100dvh;
  }
}

.painel-header,
.painel-footer {
  width: 100%;
  background-color: #2f6595;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: clamp(6px, 1.2vh, 18px) clamp(12px, 2vw, 32px);
}

.painel-header h1,
.painel-footer p {
  margin: 0;
  text-transform: uppercase;
}

.painel-header h1 {
  font-size: clamp(1.6rem, 3vw, 3.5rem);
  line-height: 1;
  letter-spacing: 0.04em;
}

.painel-footer p {
  font-size: clamp(1.1rem, 2vw, 2.25rem);
  line-height: 1.2;
}

.painel-content {
  min-width: 0;
  min-height: 0;
  overflow: auto;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  align-content: start;
  gap: clamp(12px, 1.8vh, 24px);
  padding: clamp(6px, 1.2vh, 16px) clamp(8px, 1.5vw, 24px);
  background-color: #ffffff;
}

.chamada-atual {
  min-width: 0;
  display: grid;
  gap: clamp(3px, 0.6vh, 8px);
  padding-block: clamp(16px, 2.4vh, 32px);
  text-align: center;
  text-transform: uppercase;
}

.chamada-paciente {
  margin: 0;
  padding: clamp(5px, 0.8vh, 12px) clamp(8px, 1vw, 18px);
  font-size: clamp(1.75rem, 4vw, 4.5rem);
  font-weight: 700;
  line-height: 1.05;
  overflow-wrap: anywhere;
}

.chamada-detalhe {
  margin: 0;
  color: #1976d2;
  font-size: clamp(1.1rem, 2.1vw, 2.3rem);
  font-weight: 700;
  line-height: 1.08;
  overflow-wrap: anywhere;
}

.painel-table {
  min-width: 0;
  align-self: stretch;
}

.painel-table >>> table {
  table-layout: fixed;
  width: 100%;
}

.painel-table >>> th,
.painel-table >>> td {
  height: clamp(36px, 5.2vh, 60px) !important;
  padding: 0 clamp(4px, 1vw, 16px) !important;
  font-size: clamp(0.72rem, 1.25vw, 1.4rem) !important;
  line-height: 1.08;
  white-space: normal;
  overflow-wrap: anywhere;
}

.painel-table >>> .v-datatable__progress th {
  height: 0 !important;
  padding: 0 !important;
}

.painel-table >>> th:nth-child(1),
.painel-table >>> td:nth-child(1) {
  width: 50%;
}

.painel-table >>> th:nth-child(2),
.painel-table >>> td:nth-child(2) {
  width: 20%;
}

.painel-table >>> th:nth-child(3),
.painel-table >>> td:nth-child(3) {
  width: 30%;
}

.current-call-row td:first-child {
  box-shadow: inset 4px 0 #2f6595;
}

.painel-state {
  min-height: clamp(64px, 10vh, 120px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  font-size: clamp(1rem, 1.7vw, 2rem);
  text-align: center;
}

.painel-standalone-state {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  color: #ffffff;
  background: #2f6595;
  text-align: center;
}

.sound-activation {
  position: fixed;
  inset: 0;
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(19, 39, 57, 0.94);
}

.sound-activation-card {
  width: min(620px, 100%);
  padding: clamp(24px, 5vw, 56px);
  border-radius: 8px;
  background: #ffffff;
  color: #17212b;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.sound-activation-card h1 {
  margin: 0 0 16px;
  font-size: clamp(1.8rem, 4vw, 3.4rem);
}

.sound-activation-card p {
  font-size: clamp(1rem, 2vw, 1.5rem);
}

.sound-activation-button {
  min-width: min(100%, 320px);
  min-height: 64px;
  margin-top: 12px;
  border: 0;
  border-radius: 6px;
  padding: 12px 24px;
  background: #2f6595;
  color: #ffffff;
  font-size: clamp(1.1rem, 2vw, 1.6rem);
  font-weight: 700;
  cursor: pointer;
}

.sound-activation-button:disabled {
  cursor: wait;
  opacity: 0.65;
}

.sound-silent-button {
  min-width: min(100%, 320px);
  min-height: 52px;
  margin-top: 14px;
  border: 2px solid #2f6595;
  border-radius: 6px;
  padding: 10px 20px;
  background: #ffffff;
  color: #2f6595;
  font-size: clamp(0.95rem, 1.7vw, 1.3rem);
  font-weight: 700;
  cursor: pointer;
}

.sound-silent-button:disabled {
  cursor: wait;
  opacity: 0.65;
}

.sound-countdown-help {
  margin: 10px 0 0;
  color: #52616f;
}

.sound-error {
  color: #b00020;
  font-weight: 700;
}

.connection-status {
  position: fixed;
  right: 12px;
  bottom: 12px;
  z-index: 100;
  border-radius: 999px;
  padding: 7px 12px;
  background: rgba(176, 0, 32, 0.92);
  color: #ffffff;
  font-size: clamp(0.72rem, 1vw, 0.95rem);
  font-weight: 700;
}

@media (max-height: 760px) {
  .painel-content {
    gap: 4px;
    padding-top: 5px;
    padding-bottom: 5px;
  }

  .chamada-atual {
    gap: 2px;
    padding-block: clamp(8px, 1.2vh, 14px);
  }
}
</style>
